import { useEffect, useRef, type KeyboardEvent } from 'react';
import { cn } from '@/lib/cn';

export interface PinInputProps {
  /** Controlled 4-element array of single digits (each '' or one digit). */
  digits: string[];
  onDigitsChange: (next: string[]) => void;
  disabled?: boolean;
  /** Focus the first cell shortly after mount. */
  autoFocus?: boolean;
}

/**
 * PinInput — the 4-cell digit grid shared by every parent-PIN flow (set,
 * change, and the Account-page gate). Controlled: the parent owns the
 * `digits` array so it can read the joined value, reset it, and disable the
 * grid while a request is in flight. Handles paste-to-fill, backspace
 * traversal, and arrow-key navigation between cells.
 */
export function PinInput({ digits, onDigitsChange, disabled, autoFocus }: PinInputProps) {
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (!autoFocus) return;
    // Defer a tick so the inputs are mounted (and any dialog open animation
    // has finished handing off focus) before we grab the first cell.
    const t = window.setTimeout(() => inputsRef.current[0]?.focus(), 30);
    return () => window.clearTimeout(t);
  }, [autoFocus]);

  const handleDigitChange = (index: number, raw: string) => {
    const clean = raw.replace(/\D/g, '');
    const next = [...digits];
    if (clean.length === 0) {
      next[index] = '';
      onDigitsChange(next);
      return;
    }
    for (let i = 0; i < clean.length && index + i < 4; i += 1) {
      next[index + i] = clean[i]!;
    }
    onDigitsChange(next);
    const nextFocus = Math.min(index + clean.length, 3);
    inputsRef.current[nextFocus]?.focus();
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && digits[index] === '' && index > 0) {
      inputsRef.current[index - 1]?.focus();
      const next = [...digits];
      next[index - 1] = '';
      onDigitsChange(next);
      e.preventDefault();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputsRef.current[index - 1]?.focus();
      e.preventDefault();
    } else if (e.key === 'ArrowRight' && index < 3) {
      inputsRef.current[index + 1]?.focus();
      e.preventDefault();
    }
  };

  return (
    <div className="flex items-center justify-center gap-2">
      {[0, 1, 2, 3].map((index) => (
        <input
          key={index}
          ref={(el) => {
            inputsRef.current[index] = el;
          }}
          // `password` masks the digit (shows a bullet) so an over-the-
          // shoulder onlooker can't read the parent PIN. `inputMode="numeric"`
          // keeps the numeric keypad on mobile, and paste-to-fill / backspace
          // traversal are unaffected by the input type.
          type="password"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={4}
          value={digits[index] ?? ''}
          onChange={(e) => handleDigitChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          disabled={disabled}
          aria-label={`Digit ${index + 1} of 4`}
          className={cn(
            'h-14 w-12 rounded-xl border border-or-slate-200 bg-white text-center font-display text-[22px] font-bold text-or-slate-900',
            'focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-500/30',
          )}
        />
      ))}
    </div>
  );
}
